import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { getNfseMunicipalProfile } from "@/lib/fiscal/nfse/profiles";
import { buildNfseReadiness } from "@/lib/fiscal/nfse/readiness";
import i18n from "@/i18n";

type ClinicFiscalSettingsRow = Database["public"]["Tables"]["clinic_fiscal_settings"]["Row"];
type ItemFiscalProfileRow = Pick<
  Database["public"]["Tables"]["item_fiscal_profiles"]["Row"],
  "item_id" | "nfse_service_list_item" | "nfse_iss_rate" | "nfse_service_code" | "nfse_cnae"
>;
type OwnerRow = Pick<Database["public"]["Tables"]["owners"]["Row"], "id" | "name" | "document">;
type ClinicRow = Pick<Database["public"]["Tables"]["clinics"]["Row"], "cnpj" | "name">;
type SaleRow = Pick<
  Database["public"]["Tables"]["sales"]["Row"],
  "id" | "clinic_id" | "customer_id" | "buyer_cpf" | "owner_id" | "total" | "status"
>;

interface FocusEmpresaData {
  nome?: string | null;
  cnpj?: string | null;
  inscricao_municipal?: string | null;
}

interface SaleItemWithItemType {
  id: string;
  item_id: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  item: { id: string; type: string } | null;
}

export interface NfseReviewOwnerAddress {
  street: string | null;
  number: string | null;
  neighborhood: string | null;
  zip_code: string | null;
  complement: string | null;
  city: {
    name: string | null;
    ibge_code: string | null;
    state: { code: string | null } | null;
  } | null;
}

export interface NfseReviewServiceItem {
  id: string;
  itemId: string | null;
  itemName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  fiscal: ItemFiscalProfileRow | null;
}

export function useNfseReviewData(saleId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["nfse-review", saleId],
    enabled: enabled && Boolean(saleId),
    queryFn: async () => {
      if (!saleId) return null;

      const { data: sale, error: saleError } = await supabase
        .from("sales")
        .select("id, clinic_id, customer_id, buyer_cpf, owner_id, total, status")
        .eq("id", saleId)
        .single();

      if (saleError || !sale) {
        throw saleError ?? new Error(i18n.t("nfse.review.saleNotFound", { ns: "erp" }));
      }

      const typedSale = sale as SaleRow;

      const { data: clinic, error: clinicError } = await supabase
        .from("clinics")
        .select("cnpj, name")
        .eq("id", typedSale.clinic_id)
        .maybeSingle();

      if (clinicError) throw clinicError;

      const { data: settings, error: settingsError } = await supabase
        .from("clinic_fiscal_settings")
        .select("*")
        .eq("clinic_id", typedSale.clinic_id)
        .maybeSingle();

      if (settingsError) throw settingsError;

      const { data: owner } = typedSale.owner_id
        ? await supabase
            .from("owners")
            .select("id, name, document")
            .eq("id", typedSale.owner_id)
            .maybeSingle()
        : { data: null };

      const { data: ownerAddress } = typedSale.owner_id
        ? await supabase
            .from("owners_addresses")
            .select(
              "street, number, neighborhood, zip_code, complement, city:cities(name, ibge_code, state:states(code))",
            )
            .eq("owner_id", typedSale.owner_id)
            .eq("is_primary", true)
            .maybeSingle()
        : { data: null };

      const { data: saleItems, error: itemsError } = await supabase
        .from("sale_items")
        .select("id, item_id, item_name, quantity, unit_price, line_total, item:items(id, type)")
        .eq("sale_id", saleId)
        .order("created_at", { ascending: true });

      if (itemsError) throw itemsError;

      const serviceItemsRaw = ((saleItems ?? []) as unknown as SaleItemWithItemType[]).filter(
        (line) => line.item?.type === "SERVICO",
      );
      const itemIds = serviceItemsRaw.flatMap((line) => (line.item_id ? [line.item_id] : []));

      const { data: fiscalProfiles, error: fiscalProfilesError } = itemIds.length
        ? await supabase
            .from("item_fiscal_profiles")
            .select("item_id, nfse_service_list_item, nfse_iss_rate, nfse_service_code, nfse_cnae")
            .in("item_id", itemIds)
        : { data: [], error: null };

      if (fiscalProfilesError) throw fiscalProfilesError;

      const typedSettings = settings as ClinicFiscalSettingsRow | null;
      const shouldFetchFocusEmpresa = Boolean(
        typedSettings?.focus_empresa_id &&
          (!typedSettings.cnpj || !typedSettings.company_name || !typedSettings.municipal_registration),
      );
      const { data: focusEmpresa } = shouldFetchFocusEmpresa
        ? await supabase.functions.invoke<FocusEmpresaData | { notFound: true }>(
            "fiscal-manage-empresa",
            { body: { method: "GET", focus_empresa_id: typedSettings?.focus_empresa_id } },
          )
        : { data: null };
      const typedFocusEmpresa =
        focusEmpresa && !("notFound" in focusEmpresa) ? focusEmpresa : null;
      const fiscalByItemId = new Map(
        ((fiscalProfiles ?? []) as ItemFiscalProfileRow[]).map((row) => [row.item_id, row]),
      );
      const serviceItems: NfseReviewServiceItem[] = serviceItemsRaw.map((line) => {
        const itemFiscal = line.item_id ? fiscalByItemId.get(line.item_id) ?? null : null;
        const fiscal: ItemFiscalProfileRow | null = itemFiscal || typedSettings
          ? {
              item_id: line.item_id ?? "",
              nfse_service_list_item:
                itemFiscal?.nfse_service_list_item ?? typedSettings?.default_nfse_service_list_item ?? null,
              nfse_iss_rate:
                itemFiscal?.nfse_iss_rate ?? typedSettings?.default_nfse_iss_rate ?? null,
              nfse_service_code:
                itemFiscal?.nfse_service_code ?? typedSettings?.default_nfse_service_code ?? null,
              nfse_cnae:
                itemFiscal?.nfse_cnae ?? typedSettings?.default_nfse_cnae ?? null,
            }
          : null;

        return {
          id: line.id,
          itemId: line.item_id,
          itemName: line.item_name,
          quantity: Number(line.quantity) || 0,
          unitPrice: Number(line.unit_price) || 0,
          lineTotal: Number(line.line_total) || 0,
          fiscal,
        };
      });

      const typedClinic = clinic as ClinicRow | null;
      const typedOwner = owner as OwnerRow | null;
      const typedOwnerAddress = ownerAddress as unknown as NfseReviewOwnerAddress | null;
      const municipalCode = String(
        typedSettings?.codigo_municipio_emitente ?? typedSettings?.municipal_code ?? "3550308",
      );
      const profile = getNfseMunicipalProfile(municipalCode);
      const buyerDocument = String(typedSale.buyer_cpf ?? typedOwner?.document ?? "");
      const buyerName = String(typedOwner?.name ?? "");

      const readiness = buildNfseReadiness({
        profile,
        clinic: {
          cnpj: String(typedSettings?.cnpj ?? typedFocusEmpresa?.cnpj ?? typedClinic?.cnpj ?? ""),
          companyName: String(typedSettings?.company_name ?? typedFocusEmpresa?.nome ?? typedClinic?.name ?? ""),
          municipalRegistration: String(typedSettings?.municipal_registration ?? typedFocusEmpresa?.inscricao_municipal ?? ""),
          municipalCode,
          focusApiTokenConfigured: Boolean(String(typedSettings?.focus_api_token ?? "").trim()),
          certificateConfigured: true,
        },
        buyer: {
          document: buyerDocument,
          name: buyerName,
          addressComplete: Boolean(
            typedOwnerAddress?.street &&
              typedOwnerAddress.number &&
              typedOwnerAddress.neighborhood &&
              typedOwnerAddress.zip_code,
          ),
        },
        serviceItems,
      });

      return {
        sale: typedSale,
        settings: typedSettings,
        owner: typedOwner,
        ownerAddress: typedOwnerAddress,
        profile,
        serviceItems,
        buyer: {
          document: buyerDocument,
          name: buyerName,
        },
        readiness,
      };
    },
  });
}
